import { baseActions, baseMutations, baseState, BaseState, FilterParams } from "@/store/modules/helpers/baseState";
import { resetDelayedResetTimeout } from "@/store/modules/helpers/delayedReset";
import {
    Subsets,
    subsetSetter,
    subsetGetter,
    subsetUpdateSetter,
    SubsetGetterProps,
} from "@/store/modules/helpers/baseSubset";
import { getConfig, klinikenApi } from "@/api";
import cloneDeep from "lodash/cloneDeep";
import { TypAvGenomlysningsapparat } from "@/store/modules/typAvGenomlysningsapparatModule";
import { Option } from "@/components/forms/inputs/Autocomplete/types";

export type Genomlysningsapparat = {
    id: number;
    namn: string;
    typ: number;
    typ_display?: TypAvGenomlysningsapparat;
    ordning: number;
};

type State = BaseState & {
    genomlysningsapparater: Map<Genomlysningsapparat["id"], Genomlysningsapparat>;
};

type Getters = {
    genomlysningsapparatById: (data: { id: number }) => Genomlysningsapparat | null;
};

// TODO: Ska den här verkligen vara duplicerad i enskilda moduler eller kan den importeras från baseState?
export type BaseActionsProps<T> = {
    state: BaseState & T;
    commit: <K>(
        event: "reset" | string,
        payload?: K,
        options?: {
            root?: boolean;
        }
    ) => void;
};

export const mapGenomlysningsapparat = (genomlysningsapparat: Genomlysningsapparat): Option => ({
    value: genomlysningsapparat.id,
    label: genomlysningsapparat.namn,
});

const getDefaultState: () => State = () => {
    return {
        genomlysningsapparater: new Map(),
        ...cloneDeep(baseState),
    };
};

const moduleName = "genomlysningsapparat";

export default {
    namespaced: true,
    state: getDefaultState(),
    getters: {
        genomlysningsapparater: (state: State) => [...state.genomlysningsapparater.values()],
        genomlysningsapparatById:
            (state: State) =>
            ({ id }: { id?: number }) => {
                if (!id) {
                    return null;
                }

                return state.genomlysningsapparater.get(id);
            },
        genomlysningsapparatSubset: (state: State) => (props: SubsetGetterProps<State>) =>
            subsetGetter<State>({
                ...props,
                state,
                collectionName: "genomlysningsapparater",
            }),
    },
    mutations: {
        ...baseMutations({
            reset: (state: State) => {
                Object.assign(state, getDefaultState());

                resetDelayedResetTimeout({ moduleName });
            },
        }),
        addGenomlysningsapparater: (
            state: State,
            {
                offset,
                subsetName = "default",
                genomlysningsapparater = [],
            }: {
                offset: number;
                subsetName: keyof Subsets;
                genomlysningsapparater: Genomlysningsapparat[];
            }
        ) =>
            subsetSetter({
                offset,
                subsetName,
                state,
                collectionName: "genomlysningsapparater",
                items: genomlysningsapparater,
            }),
        updateGenomlysningsapparat: (
            state: State,
            {
                genomlysningsapparater,
                offset,
                subsetName,
            }: {
                genomlysningsapparater: Genomlysningsapparat[];
                offset: number;
                subsetName: keyof Subsets;
            } // TODO subsetName kommer skickas in i framtiden
        ) =>
            subsetUpdateSetter<State, Genomlysningsapparat>({
                offset,
                state,
                collectionName: "genomlysningsapparater",
                items: genomlysningsapparater,
            }),
    },
    actions: {
        ...baseActions({ moduleName }),
        getGenomlysningsapparatById: async (
            {
                getters,
                commit,
                state,
            }: BaseActionsProps<State> & {
                getters: Getters;
            },
            {
                id,
            }: {
                id: number;
            }
        ) => {
            const foundGenomlysningsapparat = getters.genomlysningsapparatById({ id });

            if (foundGenomlysningsapparat || !id) {
                return foundGenomlysningsapparat;
            }

            try {
                const { data } = await klinikenApi.get<{
                    genomlysningsapparat: Genomlysningsapparat;
                }>(`operation/genomlysningsapparater/${id}`, getConfig({}));

                if (data) {
                    const retrievedGenomlysningsapparat = data;

                    commit("addGenomlysningsapparater", {
                        offset: 0,
                        genomlysningsapparater: [retrievedGenomlysningsapparat],
                        subsetName: id,
                    });

                    return retrievedGenomlysningsapparat;
                }

                return null;
            } catch (error) {
                console.error(error);

                throw error;
            }
        },
        /**
         * Hämta genomlysningsapparater från state eller från backend och stoppa in i state. Den returnerar inte resultatet.
         * Det du troligen vill göra är att skapa en computed som lyssnar på subsetgettern.
         * Den skapar ett nytt subset om inskickade filteringParams inte redan finns i state.
         * @param limit mängden objekt du vill få tillbaka i resultatet
         * @param page nummer för page. Används för att räkna ut offset som skickas till BE
         * @param filterParams alla sök och filtreringsparametrar. "ordering" är för sortering
         */
        fetchGenomlysningsapparater: async (
            { commit, state }: BaseActionsProps<State>,
            {
                limit = 20,
                page = 1,
                filterParams,
            }: {
                limit: number;
                page: number;
                filterParams: FilterParams;
            }
        ) => {
            try {
                const { data } = await klinikenApi.get("operation/genomlysningsapparater/", getConfig());
                const results = data.results;

                const subsetName = "default";
                const total = 0;
                const nextPage = 0;
                const offset = 0;

                if (!results) {
                    return {
                        // Namnet på subsettet (en ihopslagning av sökparams eller det du skickade in till actionen
                        subsetName, // Totala mängden träffar från sökningen
                        total, // Nummer för nästa page
                        nextPage,
                    };
                }

                commit("addGenomlysningsapparater", {
                    offset,
                    genomlysningsapparater: results,
                });

                return {
                    // Namnet på subsettet (en ihopslagning av sökparams eller det du skickade in till actionen
                    subsetName, // Totala mängden träffar från sökningen
                    total, // Nummer för nästa page
                    nextPage,
                };
            } catch (error) {
                console.error(error);

                throw error;
            }
        },
        createGenomlysningsapparat: async (
            { commit, state }: BaseActionsProps<State>,
            {
                genomlysningsapparat,
            }: {
                genomlysningsapparat: Genomlysningsapparat;
            }
        ) => {
            try {
                const response = await klinikenApi.post(
                    "operation/genomlysningsapparater/",
                    genomlysningsapparat,
                    getConfig({})
                );

                commit("addGenomlysningsapparater", {
                    offset: Object.keys(state.subsets.newlyCreated.ids).length,
                    subsetName: "newlyCreated",
                    genomlysningsapparater: [response.data],
                });
            } catch (error) {
                console.error(error);

                throw error;
            }
        },
        updateGenomlysningsapparat: async (
            { commit, state }: BaseActionsProps<State>,
            {
                genomlysningsapparat,
            }: {
                genomlysningsapparat: Genomlysningsapparat;
            }
        ) => {
            try {
                const response = await klinikenApi.put(
                    `operation/genomlysningsapparater/${genomlysningsapparat.id}/`,
                    genomlysningsapparat,
                    getConfig({})
                );

                commit("updateGenomlysningsapparat", {
                    subsetName: "newlyUpdated",
                    genomlysningsapparater: [response.data],
                    offset: Object.keys(state.subsets.newlyUpdated.ids).length,
                });
            } catch (error) {
                console.error(error);

                throw error;
            }
        },
    },
};
